// Sub-Admin Role Management — Super-admin only
import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus, Shield, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

export default function AdminSubAdmins() {
  const [subs, setSubs] = useState([]);
  const [perms, setPerms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', email: '', password: '', admin_permissions: [] });
  const [editingId, setEditingId] = useState(null);
  const [editPerms, setEditPerms] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get('/admin/rbac/permissions'),
        api.get('/admin/rbac/sub-admins'),
      ]);
      setPerms(p.data.permissions || []);
      setSubs(s.data.sub_admins || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed (Super-admin only)'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePerm = (p) => setForm((s) => ({ ...s, admin_permissions: s.admin_permissions.includes(p) ? s.admin_permissions.filter(x => x !== p) : [...s.admin_permissions, p] }));
  const toggleEditPerm = (p) => setEditPerms((s) => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);

  const create = async () => {
    if (!form.name || !form.username || !form.email || !form.password) { toast.error('All fields required'); return; }
    try {
      await api.post('/admin/rbac/sub-admin', form);
      toast.success('✅ Sub-admin created');
      setForm({ name: '', username: '', email: '', password: '', admin_permissions: [] });
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const savePerms = async (id) => {
    try {
      await api.patch(`/admin/rbac/sub-admin/${id}`, { admin_permissions: editPerms });
      toast.success('✅ Permissions updated');
      setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const demote = async (id, email) => {
    if (!window.confirm(`Demote ${email} to regular user?`)) return;
    try {
      await api.delete(`/admin/rbac/sub-admin/${id}`);
      toast.success('✅ Demoted'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="admin-sub-admins">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-black">🛡️ Sub-Admin Management</h1>
            <p className="text-xs opacity-70">Super-admin only · Create limited admins with specific permissions</p>
          </div>
          <button onClick={() => setCreating(!creating)} data-testid="sub-admin-create-toggle"
                  className="px-3 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1">
            <UserPlus size={12} /> {creating ? 'Cancel' : 'New Sub-Admin'}
          </button>
        </div>

        {creating && (
          <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/40 p-4 space-y-2" data-testid="sub-admin-create-form">
            <div className="grid grid-cols-2 gap-2">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="sub-admin-name"
                     className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="username" data-testid="sub-admin-username"
                     className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@..." data-testid="sub-admin-email"
                     className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6)" data-testid="sub-admin-password"
                     className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </div>
            <div>
              <div className="text-[10px] font-black opacity-70 mb-1">Permissions</div>
              <div className="flex flex-wrap gap-1.5">
                {perms.map((p) => (
                  <button key={p} onClick={() => togglePerm(p)} data-testid={`sub-admin-perm-${p}`}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-black ${form.admin_permissions.includes(p) ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={create} data-testid="sub-admin-create-submit"
                    className="w-full py-2 rounded-lg bg-emerald-500 text-slate-900 font-black text-xs">Create Sub-Admin</button>
          </div>
        )}

        <div className="space-y-2">
          {subs.length === 0 && <div className="text-center py-8 opacity-60 text-xs">No sub-admins yet</div>}
          {subs.map((s) => (
            <div key={s.id} className="rounded-xl bg-slate-900/70 border border-slate-800 p-3" data-testid={`sub-admin-row-${s.username}`}>
              <div className="flex items-center gap-2">
                <Shield size={14} className="text-cyan-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black truncate">{s.name} <span className="text-xs opacity-60">@{s.username}</span></div>
                  <div className="text-[10px] opacity-70">{s.email}</div>
                </div>
                <button onClick={() => { setEditingId(editingId === s.id ? null : s.id); setEditPerms(s.admin_permissions || []); }}
                        data-testid={`sub-admin-edit-${s.username}`}
                        className="px-2 py-1 rounded bg-slate-700 text-[10px] font-black">
                  {editingId === s.id ? 'Cancel' : 'Edit Perms'}
                </button>
                <button onClick={() => demote(s.id, s.email)} data-testid={`sub-admin-demote-${s.username}`}
                        className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black flex items-center gap-1">
                  <Trash2 size={10} /> Demote
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(s.admin_permissions || []).map((p) => (
                  <span key={p} className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[9px] font-black">{p}</span>
                ))}
                {(s.admin_permissions || []).length === 0 && <span className="text-[9px] opacity-50 italic">No permissions granted</span>}
              </div>
              {editingId === s.id && (
                <div className="mt-3 p-2 rounded-lg bg-slate-800/50 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {perms.map((p) => (
                      <button key={p} onClick={() => toggleEditPerm(p)}
                              data-testid={`sub-admin-editperm-${s.username}-${p}`}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-black ${editPerms.includes(p) ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-400'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => savePerms(s.id)} data-testid={`sub-admin-save-${s.username}`}
                          className="w-full py-1.5 rounded bg-yellow-400 text-slate-900 font-black text-xs flex items-center justify-center gap-1">
                    <Save size={12} /> Save Permissions
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
